import React from "react";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  LineChart, Line, AreaChart, Area,
} from "recharts";

const STATUS_COLORS = {
  active: "#004D99",
  approved: "#10B981",
  rejected: "#EF4444",
  awaiting_team_lead: "#F59E0B",
  sub_requested: "#A855F7",
};

const STATUS_LABEL = {
  active: "Aktiv",
  approved: "Təsdiqlənmiş",
  rejected: "Rədd edilmiş",
  awaiting_team_lead: "Team Lead-də",
  sub_requested: "Sub-sorğu",
};

const STAGE_LABEL = {
  site_cleaning: "Sahənin təmizlənməsi",
  fill_layers: "Dolğu qatları",
  road_base: "Yol bazası",
  asphalt_coating: "Asfalt örtüyü",
};

const STAGE_COLORS = ["#004D99", "#FF5722", "#10B981", "#F59E0B"];

const Card = ({ title, subtitle, children, testid }) => (
  <div
    className="bg-white border border-slate-200 rounded-lg p-5"
    data-testid={testid}
  >
    <div className="flex items-baseline justify-between mb-3">
      <h3 className="font-heading font-bold text-base">{title}</h3>
      {subtitle && <span className="mono text-[10px] text-slate-500">{subtitle}</span>}
    </div>
    <div className="h-64 min-h-[16rem] min-w-0">{children}</div>
  </div>
);

const TooltipBox = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-2 shadow-sm">
      {label && <div className="text-xs font-semibold mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={i} className="mono text-[11px] flex items-center gap-2">
          <span className="w-2 h-2 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

export default function AnalyticsCharts({ data }) {
  if (!data) return null;

  // Status pie
  const statusData = (data.inspections_by_status || []).map((d) => ({
    name: STATUS_LABEL[d.name] || d.name,
    value: d.value,
    key: d.name,
  }));

  // Time series
  const timeData = (data.inspections_over_time || []).map((d) => ({
    date: d.date?.slice(5),
    Yaradılan: d.created,
    Təsdiqlənən: d.approved,
    Rədd: d.rejected,
  }));

  // Cost by stage type
  const stageData = (data.stage_type_chart || []).map((d) => ({
    name: STAGE_LABEL[d.name] || d.name,
    value: d.value,
  }));
  const stageTotal = stageData.reduce((acc, d) => acc + d.value, 0);

  // Project chart
  const projectData = (data.project_chart || []).slice(0, 8).map((d) => ({
    name: d.short_name,
    Smeta: d.cost,
    İcra: d.completion,
  }));

  // Top contractors
  const contractorData = (data.contractor_chart || []).slice(0, 6);

  // Role activity stacked
  const ROLE_LABEL = {
    consulting_tech_office: "Texniki ofis",
    consulting_inspector: "İnspektor",
    consulting_topographer: "Topoqraf",
    consulting_material_engineer: "Material",
    consulting_estimate_engineer: "Smeta",
    consulting_team_lead: "Team Lead",
  };
  const roleData = (data.role_chart || []).map((r) => ({
    role: ROLE_LABEL[r.role] || r.role,
    OK: r.ok,
    "NOT OK": r.not_ok,
  }));

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="analytics-charts">
      <Card title="Yoxlanış statusu" subtitle="bütün proseslər" testid="chart-status">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={statusData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={55}
              outerRadius={85}
              paddingAngle={2}
              isAnimationActive
            >
              {statusData.map((entry, i) => (
                <Cell key={i} fill={STATUS_COLORS[entry.key] || "#94A3B8"} />
              ))}
            </Pie>
            <Tooltip content={<TooltipBox />} />
            <Legend
              iconSize={8}
              wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Yoxlanış tarixçəsi" subtitle="son 30 gün" testid="chart-timeseries">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={timeData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#004D99" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#004D99" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gApproved" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10B981" stopOpacity={0.5} />
                <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} />
            <Tooltip content={<TooltipBox />} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            <Area type="monotone" dataKey="Yaradılan" stroke="#004D99" fill="url(#gCreated)" strokeWidth={2} />
            <Area type="monotone" dataKey="Təsdiqlənən" stroke="#10B981" fill="url(#gApproved)" strokeWidth={2} />
            <Line type="monotone" dataKey="Rədd" stroke="#EF4444" strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <Card
        title="Mərhələ tipi üzrə smeta"
        subtitle={`cəmi ${stageTotal.toLocaleString()} ₼`}
        testid="chart-stage-cost"
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={stageData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={85}
              isAnimationActive
              label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""}
              labelLine={false}
            >
              {stageData.map((_, i) => (
                <Cell key={i} fill={STAGE_COLORS[i % STAGE_COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<TooltipBox />} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
          </PieChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Layihələr üzrə smeta və icra" subtitle="ilk 8 layihə" testid="chart-projects">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={projectData} margin={{ top: 8, right: 8, left: -20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 9, fontFamily: "monospace" }}
              angle={-25}
              textAnchor="end"
              interval={0}
              height={50}
            />
            <YAxis yAxisId="left" tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fontFamily: "monospace" }}
              domain={[0, 100]}
              unit="%"
            />
            <Tooltip content={<TooltipBox />} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            <Bar yAxisId="left" dataKey="Smeta" fill="#004D99" radius={[3, 3, 0, 0]} />
            <Bar yAxisId="right" dataKey="İcra" fill="#10B981" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Top Podratçılar" subtitle="reytinq %" testid="chart-contractors">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={contractorData}
            layout="vertical"
            margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fontFamily: "monospace" }} />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 10, fontFamily: "monospace" }}
              width={110}
            />
            <Tooltip content={<TooltipBox />} />
            <Bar dataKey="rating" fill="#FF5722" radius={[0, 3, 3, 0]} name="Reytinq %" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Rol fəaliyyəti" subtitle="OK vs NOT OK" testid="chart-roles">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={roleData} margin={{ top: 8, right: 8, left: -20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
            <XAxis
              dataKey="role"
              tick={{ fontSize: 9, fontFamily: "monospace" }}
              angle={-20}
              textAnchor="end"
              interval={0}
              height={40}
            />
            <YAxis tick={{ fontSize: 10, fontFamily: "monospace" }} allowDecimals={false} />
            <Tooltip content={<TooltipBox />} />
            <Legend iconSize={8} wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
            <Bar dataKey="OK" stackId="a" fill="#10B981" radius={[0, 0, 0, 0]} />
            <Bar dataKey="NOT OK" stackId="a" fill="#EF4444" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
